import React, { useEffect, useState } from 'react';
import { SlotArt, Multi, Initial, label, panel } from './BattleParts';
import { C, MEDAL, FORMAT_META, battleApi, fmtAmount, fmtMulti, slotLabel } from './battleLib';

// ============================================
// Legends — all-time slot battle records, public, across every battle that
// wasn't cancelled (GET /api/battles/leaderboard, cached server-side and
// dropped on every change). The record holders sit in spotlight cards up top;
// the full rankings share one board below with a switcher. Its own mode
// filter, independent of the page's tab.
// ============================================

const FILTERS = [['all', 'Every mode'], ['tournament', 'Tournament'], ['arena', 'Team Arena'], ['versus', 'Vs Chat']];
const BOARDS = [
  ['multis', '💥 Biggest multis'],
  ['payouts', '💸 Biggest wins'],
  ['rate', '🎯 Win rate'],
  ['wins', '⚔️ Most wins'],
  ['earners', '💰 Top earners'],
  ['titles', '🏆 Cups won'],
  ['slots', '🎰 Chat favourites'],
];

// One record holder, big.
function Spotlight({ icon, title, name, value, sub, slot }) {
  return (
    <div style={{ ...panel, padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: 0, overflow: 'hidden' }}>
      <div style={{ position: 'absolute', right: -30, top: -30, width: 120, height: 120, borderRadius: '50%', background: 'radial-gradient(circle, rgba(168,85,247,0.25), transparent 70%)' }} />
      <span style={{ ...label, display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ fontSize: '0.9rem' }}>{icon}</span>{title}</span>
      {name ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', minWidth: 0 }}>
          {slot ? <SlotArt slot={slot} width={40} radius={7} /> : <Initial name={name} size={40} />}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: C.display, fontSize: '1.25rem', fontWeight: 900, color: '#fff', lineHeight: 1.1 }}>{value}</div>
            <div style={{ fontSize: '0.8rem', fontWeight: 700, color: C.bright, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
            {sub && <div style={{ fontSize: '0.66rem', color: C.dim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</div>}
          </div>
        </div>
      ) : (
        <div style={{ fontSize: '0.78rem', color: C.dim, padding: '0.6rem 0' }}>Up for grabs</div>
      )}
    </div>
  );
}

function Rank({ i }) {
  return (
    <span style={{ width: 30, flexShrink: 0, textAlign: 'center', fontFamily: C.display, fontWeight: 900, fontSize: i < 3 ? '1rem' : '0.78rem', color: MEDAL[i + 1] || C.dim }}>
      {i + 1}
    </span>
  );
}

const rowStyle = (i) => ({
  display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.6rem 0.75rem', borderRadius: 14, listStyle: 'none',
  background: i === 0 ? 'linear-gradient(90deg, rgba(168,85,247,0.18), rgba(168,85,247,0.02))' : i % 2 ? 'rgba(255,255,255,0.02)' : 'transparent',
});
const nameStyle = { minWidth: 0, flex: 1, fontSize: '0.88rem', fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const num = { fontVariantNumeric: 'tabular-nums' };

function Board({ data, board }) {
  const Empty = ({ children = 'No records yet — the first battle sets them.' }) => (
    <p style={{ margin: 0, padding: '2rem 1rem', textAlign: 'center', fontSize: '0.82rem', color: C.dim }}>{children}</p>
  );
  const list = (items, render) => (items.length ? <ol style={{ margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>{items.map(render)}</ol> : <Empty />);

  if (board === 'multis' || board === 'payouts') {
    const items = board === 'multis' ? data.top_multis : data.top_payouts;
    return list(items, (h, i) => (
      <li key={`${h.battle_id}-${i}`} style={rowStyle(i)}>
        <Rank i={i} />
        <SlotArt slot={h.slot} width={30} radius={5} />
        <span style={{ minWidth: 0, flex: 1 }}>
          <span style={{ ...nameStyle, display: 'block' }}>{h.name}</span>
          <span style={{ display: 'block', fontSize: '0.68rem', color: C.dim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {slotLabel(h.slot)} · {FORMAT_META[h.format]?.short}
          </span>
        </span>
        {board === 'multis' ? (
          <span style={{ textAlign: 'right' }}>
            <Multi value={h.multi} size="1rem" style={{ display: 'block' }} />
            <span style={{ ...num, fontSize: '0.66rem', color: C.dim }}>{fmtAmount(h.buy)} → <span style={{ color: C.gold }}>{fmtAmount(h.payout)}</span></span>
          </span>
        ) : (
          <span style={{ textAlign: 'right' }}>
            <span style={{ ...num, display: 'block', fontFamily: C.display, fontWeight: 800, color: C.gold }}>{fmtAmount(h.payout)}</span>
            <Multi value={h.multi} size="0.7rem" />
          </span>
        )}
      </li>
    ));
  }
  if (board === 'rate') {
    if (!data.win_rates.length) return <Empty>Nobody has {data.min_fights} decided battles yet.</Empty>;
    return list(data.win_rates, (r, i) => (
      <li key={r.name} style={rowStyle(i)}>
        <Rank i={i} />
        <Initial name={r.name} size={28} />
        <span style={nameStyle}>{r.name}</span>
        <span style={{ width: 120, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ flex: 1, height: 6, borderRadius: 999, background: 'rgba(168,85,247,0.15)', overflow: 'hidden' }}>
            <span style={{ display: 'block', height: '100%', width: `${Math.round(r.rate * 100)}%`, background: `linear-gradient(90deg, ${C.violetDeep}, ${C.lav})` }} />
          </span>
          <span style={{ ...num, fontFamily: C.display, fontSize: '0.8rem', fontWeight: 800, color: '#fff' }}>{Math.round(r.rate * 100)}%</span>
        </span>
        <span style={{ ...num, width: 48, textAlign: 'right', fontSize: '0.72rem', color: C.muted }}>{r.wins}W {r.losses}L</span>
      </li>
    ));
  }
  if (board === 'wins') {
    return list(data.most_wins, (r, i) => (
      <li key={r.name} style={rowStyle(i)}>
        <Rank i={i} />
        <Initial name={r.name} size={28} />
        <span style={nameStyle}>{r.name}</span>
        <span style={{ ...num, fontSize: '0.72rem', color: C.muted }}>{r.played} battles</span>
        <span style={{ ...num, width: 40, textAlign: 'right', fontFamily: C.display, fontWeight: 900, color: '#fff' }}>{r.wins}</span>
      </li>
    ));
  }
  if (board === 'earners') {
    if (!data.top_earners.length) return <Empty>No prizes paid out yet.</Empty>;
    return list(data.top_earners, (r, i) => (
      <li key={r.name} style={rowStyle(i)}>
        <Rank i={i} />
        <Initial name={r.name} size={28} />
        <span style={nameStyle}>{r.name}</span>
        <span style={{ fontSize: '0.72rem', color: C.muted }}>{r.prizes} prize{r.prizes === 1 ? '' : 's'}</span>
        <span style={{ ...num, width: 90, textAlign: 'right', fontFamily: C.display, fontWeight: 800, color: C.gold }}>{fmtAmount(r.total)}</span>
      </li>
    ));
  }
  if (board === 'titles') {
    if (!data.champions.length) return <Empty>No cup has been won yet.</Empty>;
    return list(data.champions, (r, i) => (
      <li key={r.name} style={rowStyle(i)}>
        <Rank i={i} />
        <Initial name={r.name} size={28} />
        <span style={nameStyle}>{r.name}</span>
        <span style={{ ...num, fontFamily: C.display, fontWeight: 900, color: '#fff' }}>{r.titles} <span style={{ fontSize: '0.9rem' }}>🏆</span></span>
      </li>
    ));
  }
  // chat's favourite slots
  if (!data.top_slots.length) return <Empty />;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '0.9rem', padding: '0.25rem' }}>
      {data.top_slots.map((sl, i) => (
        <div key={sl.name} style={{ position: 'relative', textAlign: 'center', minWidth: 0 }}>
          <span style={{ position: 'absolute', left: -6, top: -6, zIndex: 1, width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: C.display, fontSize: '0.7rem', fontWeight: 900, color: '#fff', background: C.violet }}>{i + 1}</span>
          <SlotArt slot={{ name: sl.name, image: sl.image }} />
          <span style={{ display: 'block', marginTop: '0.4rem', fontSize: '0.74rem', fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sl.name}</span>
          <span style={{ ...num, display: 'block', fontSize: '0.64rem', color: C.muted }}>
            picked {sl.picks}×{sl.decided > 0 && ` · wins ${Math.round((sl.wins / sl.decided) * 100)}%`}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function BattleHallOfFame({ refreshKey }) {
  const [format, setFormat] = useState('all');
  const [data, setData] = useState(null);
  const [board, setBoard] = useState('multis');

  useEffect(() => {
    let alive = true;
    const load = () => battleApi(`/api/battles/leaderboard${format === 'all' ? '' : `?format=${format}`}`)
      .then((d) => { if (alive && d?.totals) setData(d); })
      .catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [format, refreshKey]);

  const hasHouse = data?.house && (data.house.house + data.house.chat) > 0;
  const boards = BOARDS.filter(([k]) => k !== 'titles' || !data || data.champions.length > 0 || format === 'tournament');
  const m = data?.top_multis?.[0];
  const p = data?.top_payouts?.[0];
  const w = data?.most_wins?.[0];
  const e = data?.top_earners?.[0];

  return (
    <section>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
        <div>
          <h2 style={{ margin: 0, fontFamily: C.display, fontSize: '1.5rem', fontWeight: 900, background: C.headline, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Legends</h2>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.82rem', color: C.muted }}>Every record from every slot battle, all time.</p>
        </div>
        <select value={format} onChange={(ev) => setFormat(ev.target.value)} style={{ padding: '0.5rem 0.9rem', borderRadius: 999, border: '1px solid rgba(168,85,247,0.35)', background: 'rgba(5,5,7,0.8)', color: '#fff', fontFamily: C.font, fontSize: '0.84rem', fontWeight: 700 }}>
          {FILTERS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>

      {!data ? (
        <p style={{ marginTop: '1.5rem', fontSize: '0.85rem', color: C.dim }}>Loading…</p>
      ) : (
        <>
          {/* totals as one strip */}
          <div style={{ marginTop: '1.25rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1.75rem', padding: '0.85rem 1.2rem', borderRadius: 16, background: 'rgba(168,85,247,0.07)', border: '1px solid rgba(168,85,247,0.2)' }}>
            {[
              ['battles', data.totals.battles, '#fff'],
              ['bonuses bought', data.totals.bonuses, '#fff'],
              ['paid to chat', fmtAmount(data.totals.paid), C.gold],
              ...(hasHouse ? [['snutzy : chat', `${data.house.house} : ${data.house.chat}`, '#fff']] : []),
            ].map(([k, v, color]) => (
              <span key={k} style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
                <span style={{ ...num, fontFamily: C.display, fontSize: '1.15rem', fontWeight: 900, color }}>{v}</span>
                <span style={{ fontSize: '0.74rem', color: C.muted }}>{k}</span>
              </span>
            ))}
          </div>

          <div className="sb-grid sb-4" style={{ marginTop: '1rem', gap: '0.75rem' }}>
            <Spotlight icon="💥" title="Biggest multi" name={m?.name} value={m && fmtMulti(m.multi)} sub={m && slotLabel(m.slot)} slot={m?.slot} />
            <Spotlight icon="💸" title="Biggest win" name={p?.name} value={p && <span style={{ color: C.gold }}>{fmtAmount(p.payout)}</span>} sub={p && slotLabel(p.slot)} slot={p?.slot} />
            <Spotlight icon="⚔️" title="Most wins" name={w?.name} value={w && `${w.wins} wins`} sub={w && `${w.played} battles`} />
            <Spotlight icon="💰" title="Top earner" name={e?.name} value={e && <span style={{ color: C.gold }}>{fmtAmount(e.total)}</span>} sub={e && `${e.prizes} prize${e.prizes === 1 ? '' : 's'}`} />
          </div>

          <div style={{ ...panel, marginTop: '1rem', padding: '0.75rem' }}>
            <div className="sb-scroll" style={{ display: 'flex', gap: '0.35rem', overflowX: 'auto', paddingBottom: '0.6rem', borderBottom: '1px solid rgba(168,85,247,0.15)', marginBottom: '0.6rem' }}>
              {boards.map(([k, l]) => (
                <button key={k} type="button" onClick={() => setBoard(k)} style={{
                  flexShrink: 0, borderRadius: 999, padding: '0.4rem 0.85rem', cursor: 'pointer', fontFamily: C.font, fontSize: '0.78rem', fontWeight: 700,
                  border: `1px solid ${board === k ? C.violet : 'transparent'}`, background: board === k ? 'rgba(168,85,247,0.16)' : 'transparent', color: board === k ? '#fff' : C.muted,
                }}>
                  {l}
                </button>
              ))}
            </div>
            <Board data={data} board={board} />
          </div>
        </>
      )}
    </section>
  );
}
